import { useEffect, useState } from "react";
import type { TimedHistoryEntry, TimedRecentRuns as TimedRecentRunsData } from "@mk/shared";
import { messages } from "../i18n/index.ts";
import { api } from "../lib/api.ts";
import { formatDate } from "../lib/edition.ts";
import { TIMED_DATE_OPTIONS } from "./TimedRunReport.tsx";

interface TimedRecentRunsProps {
  /** Değişince liste yeniden çekilir — biten koşu listeye girsin. */
  refreshKey: string;
  onOpen: (run: TimedHistoryEntry) => void;
}

interface RunRowProps {
  run: TimedHistoryEntry;
  record: boolean;
  onOpen: (run: TimedHistoryEntry) => void;
}

function RunRow({ run, record, onOpen }: RunRowProps) {
  return (
    <button type="button" className="history-row" data-outcome={run.wordsSolved > 0 ? "win" : "draw"} onClick={() => onOpen(run)}>
      <span className="history-row__badge">{run.wordsSolved}</span>
      <span className="history-row__main">
        <span className="history-row__name">{messages.solo.rankingScore(run.wordsSolved, run.solvedAttempts)}</span>
        <span className="history-row__date">{formatDate(run.finishedAt, TIMED_DATE_OPTIONS)}</span>
      </span>
      {record && <span className="timed-record-tag">{messages.solo.timedRecordTag}</span>}
    </button>
  );
}

/** Oyuncunun rekoru ve son beş koşusu; satıra basınca rapor açılır. */
export function TimedRecentRuns({ refreshKey, onOpen }: TimedRecentRunsProps) {
  const [data, setData] = useState<TimedRecentRunsData | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setError(false);
    api<TimedRecentRunsData>("/api/solo/timed/recent")
      .then((next) => { if (!cancelled) setData(next); })
      .catch(() => { if (!cancelled) setError(true); });
    return () => { cancelled = true; };
  }, [refreshKey]);

  return (
    <section className="timed-ranking" aria-labelledby="timed-recent-title">
      {data?.best && (
        <div className="timed-recent timed-recent--record">
          <h2 className="timed-section-title">{messages.solo.timedRecordTitle}</h2>
          <RunRow run={data.best} record={false} onOpen={onOpen} />
        </div>
      )}

      <header className="timed-ranking__head">
        <h2 id="timed-recent-title">{messages.solo.timedRecentTitle}</h2>
        {data && data.recent.length > 0 && <span>{messages.solo.timedRecentHint}</span>}
      </header>

      {error && data === null && <p className="text-sm text-muted">{messages.solo.timedRecentFailed}</p>}
      {!error && data === null && <p className="text-sm text-muted">{messages.common.loading}</p>}
      {data?.recent.length === 0 && <p className="text-sm leading-relaxed text-muted">{messages.solo.timedRecentEmpty}</p>}

      {data && data.recent.length > 0 && (
        <div className="timed-recent">
          {data.recent.map((run) => (
            <RunRow key={run.runId} run={run} record={run.runId === data.best?.runId} onOpen={onOpen} />
          ))}
        </div>
      )}
    </section>
  );
}
